import { type GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  type OurBoxesPageData,
  type Locale,
  type BoxData,
  type OurBoxesPageResponse,
  type BoxesResponse,
} from '../../lib/types';
import { PageHead } from '../../components/PageHead';
import api from '../../lib/utils/api';
import { mapOurBoxesPageData } from '../../lib/utils/mappers/map-our-boxes-page-data';
import cmsEndpoints from '../../lib/utils/cmsEndpoints';
import { mapBoxData } from '../../lib/utils/mappers/map-box-data';
import { OurBoxesFlowOriginal } from '../../components/OurBoxes/OurBoxesFlowOriginal';
import { OurBoxesFlowQuestionaire } from '../../components/OurBoxes/OurBoxesFlowQuestionaire';

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const locale = context.locale as Locale;

  const [pageData, boxesData] = await Promise.all([
    api.get<OurBoxesPageResponse>(cmsEndpoints.ourBoxesPage()),
    api.get<BoxesResponse>(cmsEndpoints.boxes()),
  ]);

  if (pageData && boxesData) {
    const page: OurBoxesPageData = mapOurBoxesPageData(
      pageData?.data?.data?.attributes,
      locale
    );
    const boxes = (boxesData?.data?.data || [])
      .map((box) => mapBoxData(box, locale) || {})
      .sort(
        (a, b) => (a.ageGroup?.minMonths ?? 0) - (b.ageGroup?.minMonths ?? 0)
      );

    return {
      props: {
        page,
        boxes,
      },
    };
  }

  return {
    notFound: true,
  };
};

type OurBoxesProps = {
  readonly page: OurBoxesPageData;
  readonly boxes: BoxData[];
};

function OurBoxes(props: OurBoxesProps) {
  const asQuestionnaire = useFeatureIsOn('our-boxes-as-questionnaire');

  const router = useRouter();

  const handleOpenBox = (boxUrl: string) => {
    router.push(`our-boxes/${boxUrl}`).catch(() => {
      console.error('Failed to navigate');
    });
  };

  return (
    <>
      <PageHead title={props.page.title} />
      {asQuestionnaire ? (
        <OurBoxesFlowQuestionaire
          boxes={props.boxes}
          page={props.page}
          onOpenBox={handleOpenBox}
        />
      ) : (
        <OurBoxesFlowOriginal
          boxes={props.boxes}
          page={props.page}
          onOpenBox={handleOpenBox}
        />
      )}
    </>
  );
}

export default OurBoxes;
